/* eslint-disable camelcase */
import React, { useState } from 'react';

import { useRouter } from 'next/router';
import styled from 'styled-components';

import useGender from '~/shared/utils/useGender';
import { useDomain } from '~/techstyle-shared/redux-core';

import { CUSTOM_COLOR_FILTER } from '../../constants';
import { useFilterAvailability } from '../../context/FilterAvailabilityContext';
import { useFiltersPanel } from '../../context/FiltersPanelContext';
import { useGridFacets } from '../../context/GridDataContexts';
import useGridInputs from '../../hooks/useGridInputs';
import useMaxNumQuickFiltersToRender from '../../hooks/useMaxNumQuickFiltersToRender';
import {
  clearAllFacetsFromUrlParams,
  getTransformedSelectedFilters,
  updateFiltersInUrlParams,
} from '../../utils/filters';
import { QueryParamBehavior } from '../../utils/routing';

import FilterControls from './FilterControls';
import FiltersPanelModal from './FiltersPanelModal';
import SelectedFiltersPanel from './SelectedFiltersPanel';

const Container = styled.div`
  margin-top: 16px;
`;

const FilterBar = () => {
  // Data hooks
  const {
    facets,
    selectedUrlFilters,
    allRegisteredFacets,
    allRegisteredFacetNames,
    sortOptions,
  } = useGridFacets();
  const { baseFacets } = useFilterAvailability();
  const domain = useDomain();
  const { gender } = useGender();
  const { gridType } = useGridInputs();
  const router = useRouter();
  const maxNumQuickFiltersToRender = useMaxNumQuickFiltersToRender();

  // Quick Filters render from the static baseFacets so their position/order
  // doesn't shift as users apply filters (FLCE-3412). Fall back to the dynamic
  // facets when baseFacets isn't available (Products V2, lean query not loaded).
  const quickFilterFacets = baseFacets ?? facets;

  // State
  const { isFiltersPanelOpen, openFiltersPanel, closeFiltersPanel } =
    useFiltersPanel();
  const [activeFacetName, setActiveFacetName] = useState<string | null>(null);

  // Early return if no filters or sort options
  if (!quickFilterFacets?.length && !sortOptions?.length) {
    return null;
  }

  // Computed values
  const selectedFacets = getTransformedSelectedFilters({
    selectedUrlFilters,
    allRegisteredFacets,
    gridType,
    domain,
    gender,
  });

  const allClearableFacetNames = [
    ...allRegisteredFacetNames,
    CUSTOM_COLOR_FILTER.name,
  ];

  // Event handlers
  const handleOnChange = ({ facetName, facetOption }) => {
    updateFiltersInUrlParams({
      facetName,
      facetOption,
      router,
      queryParamBehavior: QueryParamBehavior.TOGGLE,
    });
  };

  const handleClearAllFilters = () => {
    clearAllFacetsFromUrlParams({
      router,
      allRegisteredFacetNames: allClearableFacetNames,
      gridType,
    });
  };

  const expandFilterDropdown = (facetName: string) => {
    setActiveFacetName(facetName);
  };

  const handleCloseFiltersPanel = () => {
    closeFiltersPanel();
    setActiveFacetName(null);
  };

  return (
    <Container>
      <FilterControls
        openFiltersPanel={openFiltersPanel}
        expandFilterDropdown={expandFilterDropdown}
        facets={quickFilterFacets}
        maxNumQuickFiltersToRender={maxNumQuickFiltersToRender}
        selectedFacets={selectedFacets}
        handleClearAllFilters={handleClearAllFilters}
      />

      <SelectedFiltersPanel
        selectedFacets={selectedFacets}
        handleOnChange={handleOnChange}
      />

      <FiltersPanelModal
        isOpen={isFiltersPanelOpen}
        onClose={handleCloseFiltersPanel}
        activeFacetName={activeFacetName}
      />
    </Container>
  );
};

FilterBar.propTypes = {};

// Memoize so products-driven re-renders in the parent grid view don't cascade
// into this component. FilterBar only subscribes to FacetsContext.
export default React.memo(FilterBar);
